import React, { useRef, useLayoutEffect, useMemo } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import Login from '../../screens/auth/LoginScreen';
import Register from '../../screens/auth/Register';
import Verification from '../../screens/auth/Verification';
import BusinessDetailsScreen from '../../screens/auth/BusinessDetailsScreen';
import WelcomeScreen from '../../screens/auth/WelcomeScreen';
import {
  StyleSheet,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from 'react-native';
import Typography from '../../components/Reusables/Typography';
import { forceHttps, fs, v, s } from '../../utils/libs';
import NoInternetScreen from '../../screens/NoInternetScreen';
import NetworkMonitor from '../../components/NetworkMonitor';
import { useTheme, GTFonts } from '../../theme';
import FastImage from '@d11/react-native-fast-image';
import { WELCOME_WALL_IMAGES } from '../../constants/onboarding';
import FaqScreen from '../../screens/faq/FaqScreen';
import DemoStack from '../demo/DemoStack';
import OnboardingScreen from '../../screens/auth/OnboardingScreen';
import { ResetPasscodeProvider } from '../../Context/ResetPasscodeContext';
import ResetPasscodeRequestScreen from '../../screens/auth/ResetPasscodeRequestScreen';
import ResetPasscodeVerifyCodeScreen from '../../screens/auth/ResetPasscodeVerifyCodeScreen';
import ResetPasscodeNewPasscodeScreen from '../../screens/auth/ResetPasscodeNewPasscodeScreen';
import HeaderActions from '../../components/Reusables/headers/HeaderActions';
import useChatwootSnapshot from '../../hooks/useChatwootSnapshot';
import ChatwootService from '../../services/ChatwootService';

const Stack = createNativeStackNavigator();

const AuthNav = () => {
  const { theme, isDark } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 768;
  const didPreloadOnboarding = useRef(false);
  const chatwootSnapshot = useChatwootSnapshot();

  const supportHeaderActions = useMemo(
    () => [
      {
        type: 'icon' as const,
        icon: 'chatbubbles-sharp' as const,
        iconLibrary: 'ionicons' as const,
        badgeCount: chatwootSnapshot.unread.unreadCount,
        showBadge: chatwootSnapshot.unread.hasUnread,
        onPress: () => void ChatwootService.openChat(),
        accessibilityLabel: 'Customer support',
        visible: ChatwootService.isConfigured(),
      },
    ],
    [chatwootSnapshot.unread.hasUnread, chatwootSnapshot.unread.unreadCount],
  );

  // Preload onboarding media ASAP regardless of onboarding completion
  useLayoutEffect(() => {
    if (didPreloadOnboarding.current) return;
    didPreloadOnboarding.current = true;
    const urls = WELCOME_WALL_IMAGES.filter(Boolean) as string[];
    if (!urls.length) return;
    const [first, ...rest] = urls;
    const toPreload = [
      { uri: forceHttps(first), priority: FastImage.priority.high },
      ...rest.map(u => ({
        uri: forceHttps(u),
        priority: FastImage.priority.normal,
      })),
    ];
    try {
      FastImage.preload(toPreload);
    } catch (e) {
      // noop
    }
  }, []);

  return (
    <>
      <NetworkMonitor />
      <ResetPasscodeProvider>
        <Stack.Navigator
          initialRouteName={'Onboarding'}
          screenOptions={{
            gestureDirection: 'horizontal',
            headerStyle: {
              backgroundColor: '#1560bd',
            },
            headerTintColor: 'white',
            headerTitleStyle: {
              fontSize: fs(18),
            },
          }}>
          <Stack.Screen
            name="Onboarding"
            component={WelcomeScreen}
            initialParams={{ onboardingColor: '#fff' }}
            options={{
              headerShown: false,
            }}
          />
          <Stack.Screen
            name="DemoStack"
            component={DemoStack}
            options={{
              headerShown: false,
            }}
          />
          <Stack.Screen
            name="OnboardingCarousel"
            component={OnboardingScreen}
            options={{
              headerShown: false,
              gestureEnabled: false,
              headerBackVisible: false,
            }}
          />
          <Stack.Screen
            name="Login"
            component={Login}
            options={({ navigation }) => ({
              headerShown: !(Platform.OS === 'web' && isLargeScreen),
              headerLeft: () => {
                return null;
              },
              headerTitle: () => {
                return (
                  <TouchableOpacity
                    onPress={() => navigation.navigate('AuthFaq')}>
                    <Typography style={styles.topText1}>
                      Why BizPlus POS?
                    </Typography>
                  </TouchableOpacity>
                );
              },
              headerRight: () => <HeaderActions actions={supportHeaderActions} />,
              headerShadowVisible: false,
              headerStyle: {
                backgroundColor: '#1560bd',
              },
              headerTintColor: 'white',
            })}></Stack.Screen>
          <Stack.Screen
            name="AuthFaq"
            component={FaqScreen}
            options={() => ({
              headerShown: !(Platform.OS === 'web' && isLargeScreen),
              title: 'BizPlus POS Solution',
              headerStyle: {
                backgroundColor: '#1560bd',
              },
              headerTintColor: 'white',
            })}
          />
          <Stack.Screen
            name="Register"
            component={Register}
            options={() => ({
              headerShown: !(Platform.OS === 'web' && isLargeScreen),
              title: 'Create Account',
              headerRight: () => <HeaderActions actions={supportHeaderActions} />,
              headerStyle: {
                backgroundColor: '#1560bd',
              },
              headerTintColor: 'white',
            })}></Stack.Screen>
          <Stack.Screen
            name="Verification"
            component={Verification}
            options={() => ({
              headerShown: !(Platform.OS === 'web' && isLargeScreen),
              title: 'Verify Account',
              headerRight: () => <HeaderActions actions={supportHeaderActions} />,
              headerStyle: {
                backgroundColor: '#1560bd',
              },
              headerTintColor: 'white',
            })}></Stack.Screen>
          <Stack.Screen
            name="BusinessDetailsScreen"
            component={BusinessDetailsScreen}
            options={() => ({
              headerShown: !(Platform.OS === 'web' && isLargeScreen),
              title: 'Business Details',
              headerRight: () => <HeaderActions actions={supportHeaderActions} />,
              headerStyle: {
                backgroundColor: '#1560bd',
              },
              headerTintColor: 'white',
            })}></Stack.Screen>
          <Stack.Screen
            name="ResetPasscodeRequest"
            component={ResetPasscodeRequestScreen}
            options={() => ({
              headerShown: !(Platform.OS === 'web' && isLargeScreen),
              title: 'Reset passcode',
              headerRight: () => <HeaderActions actions={supportHeaderActions} />,
              headerStyle: {
                backgroundColor: '#1560bd',
              },
              headerTintColor: 'white',
            })}
          />
          <Stack.Screen
            name="ResetPasscodeVerifyCode"
            component={ResetPasscodeVerifyCodeScreen}
            options={() => ({
              headerShown: !(Platform.OS === 'web' && isLargeScreen),
              title: 'Verify code',
              headerRight: () => <HeaderActions actions={supportHeaderActions} />,
              headerStyle: {
                backgroundColor: '#1560bd',
              },
              headerTintColor: 'white',
            })}
          />
          <Stack.Screen
            name="ResetPasscodeNewPasscode"
            component={ResetPasscodeNewPasscodeScreen}
            options={() => ({
              headerShown: !(Platform.OS === 'web' && isLargeScreen),
              title: 'New passcode',
              headerRight: () => <HeaderActions actions={supportHeaderActions} />,
              headerStyle: {
                backgroundColor: '#1560bd',
              },
              headerTintColor: 'white',
            })}
          />
          <Stack.Screen
            name="NoInternetScreen"
            component={NoInternetScreen}
            options={{
              headerShown: false,
              presentation: 'modal',
              animation: 'fade',
              statusBarStyle: isDark ? 'light' : 'dark',
              statusBarBackgroundColor: theme.colors.background,
            }}
          />
        </Stack.Navigator>
      </ResetPasscodeProvider>
    </>
  );
};

export default AuthNav;

const styles = StyleSheet.create({
  topText: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: s(20),
    marginTop: v(10),
  },
  topText1: {
    textDecorationLine: 'underline',
    fontSize: fs(16),
    lineHeight: fs(20),
    color: '#fff',
    fontFamily: GTFonts.semibold,
  },
});
